import type { ActivityEvent } from '../components/Dashboard/activityFeedApi';
import type { CandidateAnalytics } from '../types/candidateAnalytics';
import { activityCategoryShortLabel } from './activityFeedFormat';

export type ActivityFilterTab = 'all' | 'views' | 'messages' | 'questions' | 'references' | 'likes';

export type DateRangePreset = '7d' | '14d' | '30d';

const READ_IDS_KEY = 'skipscreen_activity_read_ids';

export function getDateRange(preset: DateRangePreset): {
  start: Date;
  end: Date;
} {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);
  const days = preset === '7d' ? 7 : preset === '14d' ? 14 : 30;
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

export function formatDateRangeLabel(start: Date, end: Date): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const yearFmt = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  if (start.getFullYear() === end.getFullYear()) {
    return `${fmt(start)} – ${yearFmt(end)}`;
  }
  return `${yearFmt(start)} – ${yearFmt(end)}`;
}

export function matchesActivityFilter(category: string, tab: ActivityFilterTab): boolean {
  if (tab === 'all') return true;
  if (tab === 'views') {
    return [
      'recruiter_viewed_profile',
      'recruiter_profile_dwell',
      'recruiter_expanded_section',
    ].includes(category);
  }
  if (tab === 'messages') return category === 'recruiter_message_received';
  if (tab === 'questions') return category === 'recruiter_chat_request';
  if (tab === 'references') {
    return ['user_reference_added', 'user_reference_confirmed'].includes(category);
  }
  if (tab === 'likes') return category === 'recruiter_liked_section';
  return true;
}

export function isEventInRange(event: ActivityEvent, start: Date, end: Date): boolean {
  const d = new Date(event.created_at);
  return d >= start && d <= end;
}

export function activityCenterTitle(category: string): string {
  const map: Record<string, string> = {
    recruiter_viewed_profile: 'Profile Viewed',
    recruiter_profile_dwell: 'Profile Engagement',
    recruiter_expanded_section: 'Section Expanded',
    recruiter_liked_section: 'Section Liked',
    recruiter_chat_request: 'Recruiter Question',
    recruiter_message_received: 'New Message Received',
    user_reference_confirmed: 'Reference Confirmed',
    user_reference_added: 'Reference Added',
    recruiter_meeting_request: 'Interview Request',
    recruiter_access_request: 'Access Request',
    recruiter_saved_profile: 'Profile Saved',
    recruiter_prompt_video_watch: 'Prompt Video Watched',
  };
  return map[category] || activityCategoryShortLabel(category);
}

export function activityCenterSubLinkLabel(category: string): string {
  if (category === 'recruiter_chat_request') return 'Answer now';
  if (category === 'recruiter_message_received') return 'Open conversation';
  if (category.includes('reference')) return 'View reference';
  if (
    category.includes('view') ||
    category.includes('like') ||
    category.includes('dwell') ||
    category.includes('expand')
  ) {
    return 'View analytics';
  }
  return 'View details';
}

export function activityCenterActionButton(category: string): {
  label: string;
  primary: boolean;
} {
  if (category === 'recruiter_chat_request') {
    return { label: 'Answer Question', primary: true };
  }
  if (category === 'recruiter_message_received') {
    return { label: 'Open Conversation', primary: false };
  }
  if (category.includes('reference')) {
    return { label: 'See Details', primary: false };
  }
  if (
    category.includes('view') ||
    category.includes('like') ||
    category.includes('dwell') ||
    category.includes('expand')
  ) {
    return { label: 'View Analytics', primary: false };
  }
  return { label: 'See Details', primary: false };
}

export function activityUnreadDotClass(category: string): string {
  if (category === 'recruiter_liked_section') return 'ac-dot ac-dot--rose';
  if (category === 'recruiter_message_received' || category === 'recruiter_chat_request') {
    return 'ac-dot ac-dot--blue';
  }
  return 'ac-dot ac-dot--green';
}

function countEvents(
  events: ActivityEvent[],
  start: Date,
  end: Date,
  predicate: (category: string) => boolean
): number {
  return events.filter((e) => isEventInRange(e, start, end) && predicate(e.category)).length;
}

export function computeWeeklyChange(
  current: number,
  previous: number
): { pct: number; label: string; tone: 'up' | 'down' | 'flat' } {
  if (previous === 0) {
    if (current === 0) return { pct: 0, label: '0% this week', tone: 'flat' };
    return { pct: 100, label: '+100% this week', tone: 'up' };
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { pct: 0, label: '0% this week', tone: 'flat' };
  const sign = pct > 0 ? '+' : '';
  return {
    pct,
    label: `${sign}${pct}% this week`,
    tone: pct > 0 ? 'up' : 'down',
  };
}

export type ActivityCenterMetrics = {
  profileViews: number;
  profileViewsChange: ReturnType<typeof computeWeeklyChange>;
  messages: number;
  messagesChange: ReturnType<typeof computeWeeklyChange>;
  questions: number;
  questionsChange: ReturnType<typeof computeWeeklyChange>;
  likes: number;
  likesChange: ReturnType<typeof computeWeeklyChange>;
  interestScore: number;
  interestChange: ReturnType<typeof computeWeeklyChange>;
  topSectionLabel: string;
};

export function buildActivityCenterMetrics(
  events: ActivityEvent[],
  analytics: CandidateAnalytics | null,
  range: { start: Date; end: Date }
): ActivityCenterMetrics {
  const msDay = 86400000;
  const rangeDays = Math.round((range.end.getTime() - range.start.getTime()) / msDay) + 1;
  const prevEnd = new Date(range.start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  prevEnd.setHours(23, 59, 59, 999);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (rangeDays - 1));
  prevStart.setHours(0, 0, 0, 0);

  const viewPred = (c: string) => matchesActivityFilter(c, 'views');
  const msgPred = (c: string) => c === 'recruiter_message_received';
  const qPred = (c: string) => c === 'recruiter_chat_request';
  const likePred = (c: string) => c === 'recruiter_liked_section';

  const viewsCurrent = countEvents(events, range.start, range.end, viewPred);
  const viewsPrev = countEvents(events, prevStart, prevEnd, viewPred);
  const msgCurrent = countEvents(events, range.start, range.end, msgPred);
  const msgPrev = countEvents(events, prevStart, prevEnd, msgPred);
  const qCurrent = countEvents(events, range.start, range.end, qPred);
  const qPrev = countEvents(events, prevStart, prevEnd, qPred);
  const likeCurrent = countEvents(events, range.start, range.end, likePred);
  const likePrev = countEvents(events, prevStart, prevEnd, likePred);

  const profileViews = viewsCurrent || Number(analytics?.engagement.profile_views ?? 0);
  const messages = msgCurrent || Number(analytics?.engagement.chat_requests_accepted ?? 0);
  const questions = qCurrent || Number(analytics?.engagement.question_requests ?? 0);
  const likes = likeCurrent || Number(analytics?.engagement.section_likes ?? 0);
  const interestScore = Math.round(Number(analytics?.overview.profile_strength ?? 0));

  const topSectionLabel = resolveTopSectionLabel(events, analytics);

  return {
    profileViews,
    profileViewsChange: computeWeeklyChange(viewsCurrent, viewsPrev),
    messages,
    messagesChange: computeWeeklyChange(msgCurrent, msgPrev),
    questions,
    questionsChange: computeWeeklyChange(qCurrent, qPrev),
    likes,
    likesChange: computeWeeklyChange(likeCurrent, likePrev),
    interestScore,
    interestChange: computeWeeklyChange(
      viewsCurrent + likeCurrent + qCurrent,
      viewsPrev + likePrev + qPrev
    ),
    topSectionLabel,
  };
}

function resolveTopSectionLabel(
  events: ActivityEvent[],
  analytics: CandidateAnalytics | null
): string {
  const counts = new Map<string, number>();
  for (const ev of events) {
    if (!['recruiter_expanded_section', 'recruiter_liked_section'].includes(ev.category)) {
      continue;
    }
    const label = String(ev.metadata?.target_label ?? ev.metadata?.target_key ?? '').trim();
    if (!label) continue;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  let top = '';
  let topCount = 0;
  for (const [label, count] of Array.from(counts.entries())) {
    if (count > topCount) {
      top = label;
      topCount = count;
    }
  }
  if (top) return top;
  const prompt = analytics?.prompts?.[0]?.title;
  return prompt || 'Your prompts';
}

export function loadReadActivityIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_IDS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

export function saveReadActivityIds(ids: Set<string>) {
  localStorage.setItem(READ_IDS_KEY, JSON.stringify(Array.from(ids)));
}
